import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
  Divider,
  Table,
  Modal,
  InputNumber,
  Switch,
  Progress,
  Alert,
  Upload,
} from "antd";
import { useUpdate, useList, useOne } from "@refinedev/core";
import { useParams, useNavigate } from "react-router-dom";
import { SubtitleManager } from "../../components/content/SubtitleManager";
import {
  UploadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Content, Season, Episode } from "@/types";
import { MediaUploader } from "@/components/media/MediaUploader";
import { ProcessingStatusBadge } from "@/components/media/ProcessingStatusBadge";
import { useProcessingStatus } from "@/hooks/useProcessingStatus";
import { apiService } from "@/services/api";
import { SelecteurLangue } from "@/components/content/SelecteurLangue";
import {
  SelecteurAgeMinimum,
  SelecteurDescripteurs,
} from "@/components/content/SelecteurClassification";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export const ContentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  /**
   * Statut du contenu tel qu'il est en base.
   *
   * La classification n'est exigée qu'au **passage** à « Publié ». Un contenu
   * déjà en ligne et non classé doit rester corrigible : bloquer une
   * correction de titre sur une classification manquante serait plus strict
   * que le serveur, qui l'autorise.
   */
  const [statutInitial, setStatutInitial] = useState<string | undefined>();
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingTrailer, setUploadingTrailer] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  const [episodeUploadProgress, setEpisodeUploadProgress] = useState(0);
  const [videoFileList, setVideoFileList] = useState<any[]>([]);
  const [trailerFileList, setTrailerFileList] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [seasonModalVisible, setSeasonModalVisible] = useState(false);
  const [episodeModalVisible, setEpisodeModalVisible] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);
  const [editingEpisode, setEditingEpisode] = useState<Episode | null>(null);
  const [episodeVideoFile, setEpisodeVideoFile] = useState<File | null>(null);
  const [uploadingEpisodeVideo, setUploadingEpisodeVideo] = useState(false);
  const [posterFileList, setPosterFileList] = useState<any[]>([]);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [posterUploadProgress, setPosterUploadProgress] = useState(0);
  const [backdropFileList, setBackdropFileList] = useState<any[]>([]);
  const [uploadingBackdrop, setUploadingBackdrop] = useState(false);
  const [backdropUploadProgress, setBackdropUploadProgress] = useState(0);

  const { mutate: updateContent } = useUpdate();

  const { data: contentData, isLoading: contentLoading } = useOne<Content>({
    resource: "contents",
    id: id!,
  });

  const { data: genresData } = useList({
    resource: "genres",
    pagination: { mode: "off" },
  });

  const { data: typesData } = useList({
    resource: "types",
    pagination: { mode: "off" },
  });

  const content = contentData?.data;

  const { data: processingStatus } = useProcessingStatus(
    content?.id ? Number(content.id) : null
  );

  useEffect(() => {
    if (content) {
      setStatutInitial(content.status);
      form.setFieldsValue({
        title: content.title,
        description: content.overview,
        releaseDate: content.release_date ? dayjs(content.release_date) : null,
        status: content.status,
        typeId: content.type?.id,
        genreIds: content.genres?.map((g) => g.id),
        tags: content.tags,
        originalLanguage: content.originalLanguage,
        hasSubtitles: content.hasSubtitles,
        hasMultiAudio: content.hasMultiAudio,
        // Classification par âge (CMP-03)
        ageRating: content.ageRating,
        contentDescriptors: content.contentDescriptors,
        // Annonce (avant-première, événement)
        isAnnouncement: content.isAnnouncement,
        announcementLabel: content.announcementLabel,
        announcementVenue: content.announcementVenue,
        announcementDate: content.announcementDate ? dayjs(content.announcementDate) : null,
        // Champs spécifiques aux films
        duration: content.movie?.duration,
        director: content.movie?.director,
        budget: content.movie?.budget,
        boxOffice: content.movie?.boxOffice,
        // Champs spécifiques aux séries
        totalSeasons: content.series?.totalSeasons,
        totalEpisodes: content.series?.totalEpisodes,
        averageEpisodeDuration: content.series?.averageEpisodeDuration,
      });
      setSelectedType(content.type?.title || "");

      // Charger les saisons et épisodes si c'est une série
      if (content.type?.title === "SERIES" && content.series) {
        loadSeasonsAndEpisodes(content!.id);
      }
    }
  }, [content, form]);

  const loadSeasonsAndEpisodes = async (contentId: number) => {
    try {
      // Charger les saisons et épisodes depuis l'API
      const seasonsData = await apiService.getSeasons(contentId);
      setSeasons(seasonsData);

      // Charger les épisodes pour chaque saison
      const allEpisodes: Episode[] = [];
      for (const season of seasonsData) {
        const episodesData = await apiService.getEpisodes(season.id);
        allEpisodes.push(...episodesData);
      }
      setEpisodes(allEpisodes);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des saisons et épisodes:",
        error
      );
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Préparer le payload au format ContentRequestDTO attendu par le backend
      const payload: any = {
        typeId: values.typeId,
        title: values.title?.trim(),
        status: values.status || "DRAFT",
        description: values.description?.trim(),
        releaseDate: values.releaseDate,
        genreIds: values.genreIds || [],
        tags:
          values.tags
            ?.map((tag: string) => tag.trim())
            .filter((tag: string) => tag.length > 0) || [],
        originalLanguage: values.originalLanguage,
        hasSubtitles: Boolean(values.hasSubtitles),
        hasMultiAudio: Boolean(values.hasMultiAudio),
        // Omis plutôt qu'envoyés à vide : le service ne remplace la
        // classification que si la requête la porte, et une valeur vide
        // effacerait une note déjà saisie.
        ...(values.ageRating !== undefined &&
          values.ageRating !== null && { ageRating: values.ageRating }),
        ...(values.contentDescriptors && {
          contentDescriptors: values.contentDescriptors,
        }),
        // Annonce : ces informations étaient écrites en dur dans l'application
        isAnnouncement: Boolean(values.isAnnouncement),
        announcementLabel: values.announcementLabel || null,
        announcementVenue: values.announcementVenue || null,
        announcementDate: values.announcementDate
          ? values.announcementDate.format("YYYY-MM-DDTHH:mm:ss")
          : null,
      };

      // Ajouter les champs spécifiques selon le type
      if (selectedType === "MOVIE") {
        payload.movie = {
          duration: values.duration,
          director: values.director?.trim(),
          budget: values.budget,
          boxOffice: values.boxOffice,
        };
      } else if (selectedType === "SERIES") {
        payload.series = {
          totalSeasons: values.totalSeasons,
          totalEpisodes: values.totalEpisodes,
          averageEpisodeDuration: values.averageEpisodeDuration,
        };
      }

      console.log("Payload envoyé au backend:", payload);

      updateContent(
        {
          resource: "contents",
          id: content!.id,
          values: payload,
          successNotification: () => ({
            message: "Contenu mis à jour",
            description: "Le contenu a été mis à jour avec succès",
            type: "success",
          }),
          errorNotification: (error: any) => {
            console.error("Erreur détaillée de mise à jour:", error);
            return {
              message: "Erreur lors de la mise à jour",
              description: error?.message || "Une erreur est survenue",
              type: "error",
            };
          },
        },
        {
          onSuccess: () => {
            console.log("Mise à jour réussie!");
            setVideoFileList([]);
            setTrailerFileList([]);
            setLoading(false);
            message.success("Contenu mis à jour avec succès!");
            // Recharger les données
            window.location.reload();
          },
          onError: (error: any) => {
            console.error("Erreur lors de la mise à jour du contenu:", error);
            message.error(
              error?.response?.data?.message ||
                error?.message ||
                "Erreur lors de la mise à jour du contenu"
            );
            setLoading(false);
          },
        }
      );
    } catch (error: any) {
      console.error("Exception lors de la mise à jour:", error);
      message.error(
        error?.message || "Erreur lors de la mise à jour du contenu"
      );
      setLoading(false);
    }
  };

  const videoUploadProps = {
    beforeUpload: (file: File) => {
      setVideoFileList([file]);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setVideoFileList([]);
    },
    fileList: videoFileList as any,
  };

  const trailerUploadProps = {
    beforeUpload: (file: File) => {
      setTrailerFileList([file]);
      return false; // prevent auto upload
    },
    onRemove: () => {
      setTrailerFileList([]);
    },
    fileList: trailerFileList as any,
  };

  const backdropUploadProps = {
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("Seuls les fichiers image sont acceptés");
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("L'image ne doit pas dépasser 10 Mo");
        return false;
      }
      setBackdropFileList([file]);
      return false;
    },
    onRemove: () => {
      setBackdropFileList([]);
    },
    fileList: backdropFileList as any,
  };

  const handleBackdropUpload = async () => {
    if (!content) {
      message.warning("Contenu non trouvé");
      return;
    }
    if (backdropFileList.length === 0) {
      message.warning("Sélectionnez d'abord une image");
      return;
    }

    try {
      setUploadingBackdrop(true);
      setBackdropUploadProgress(0);

      await apiService.uploadBackdrop(
        content.id,
        backdropFileList[0] as File,
        (progress) => {
          setBackdropUploadProgress(progress);
        }
      );

      setBackdropUploadProgress(100);
      message.success("Backdrop uploadé avec succès");
      setBackdropFileList([]);
      window.location.reload();
    } catch (error: any) {
      console.error("Erreur lors de l'upload du backdrop:", error);
      message.error(
        error?.response?.data?.message || "Erreur lors de l'upload du backdrop"
      );
    } finally {
      setUploadingBackdrop(false);
      setBackdropUploadProgress(0);
    }
  };

  const posterUploadProps = {
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("Seuls les fichiers image sont acceptés");
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("L'image ne doit pas dépasser 10 Mo");
        return false;
      }
      setPosterFileList([file]);
      return false;
    },
    onRemove: () => {
      setPosterFileList([]);
    },
    fileList: posterFileList as any,
  };

  const handlePosterUpload = async () => {
    if (!content) {
      message.warning("Contenu non trouvé");
      return;
    }
    if (posterFileList.length === 0) {
      message.warning("Sélectionnez d'abord une image");
      return;
    }

    try {
      setUploadingPoster(true);
      setPosterUploadProgress(0);

      const result = await apiService.uploadPoster(
        content.id,
        posterFileList[0] as File,
        (progress) => {
          setPosterUploadProgress(progress);
        }
      );

      setPosterUploadProgress(100);
      message.success("Affiche uploadée avec succès");
      setPosterFileList([]);
      // Recharger pour afficher la nouvelle affiche
      window.location.reload();
    } catch (error: any) {
      console.error("Erreur lors de l'upload de l'affiche:", error);
      message.error(
        error?.response?.data?.message || "Erreur lors de l'upload de l'affiche"
      );
    } finally {
      setUploadingPoster(false);
      setPosterUploadProgress(0);
    }
  };

  const handleVideoUpload = async () => {
    if (!content) {
      message.warning("Contenu non trouvé");
      return;
    }
    if (videoFileList.length === 0) {
      message.warning("Sélectionnez d'abord un fichier vidéo");
      return;
    }

    try {
      setUploadingVideo(true);
      setVideoUploadProgress(0);
      message.info("Upload de la vidéo complète en cours...");

      await apiService.uploadVideo(
        content.id,
        videoFileList[0] as File,
        (progress) => {
          setVideoUploadProgress(progress);
        }
      );

      setVideoUploadProgress(100);
      message.success("Vidéo uploadée ! Le transcodage va commencer automatiquement.");
      setVideoFileList([]);
    } catch (error: any) {
      console.error("Erreur lors de l'upload de la vidéo:", error);
      message.error(
        error?.response?.data?.message || "Erreur lors de l'upload de la vidéo"
      );
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
    }
  };

  const handleTrailerUpload = async () => {
    if (!content) {
      message.warning("Contenu non trouvé");
      return;
    }
    if (trailerFileList.length === 0) {
      message.warning("Sélectionnez d'abord un fichier de bande d'annonce");
      return;
    }

    try {
      setUploadingTrailer(true);
      setTrailerUploadProgress(0);
      message.info("Upload de la bande d'annonce en cours...");

      await apiService.uploadTrailer(
        content.id,
        trailerFileList[0] as File,
        (progress) => {
          setTrailerUploadProgress(progress);
        }
      );

      setTrailerUploadProgress(100);
      message.success("Bande d'annonce uploadée ! Le transcodage va commencer automatiquement.");
      setTrailerFileList([]);
    } catch (error: any) {
      console.error("Erreur lors de l'upload de la bande d'annonce:", error);
      message.error(
        error?.response?.data?.message ||
          "Erreur lors de l'upload de la bande d'annonce"
      );
    } finally {
      setUploadingTrailer(false);
      setTrailerUploadProgress(0);
    }
  };

  const handleSeasonSubmit = async (values: any) => {
    try {
      if (editingSeason) {
        // Mise à jour
        await apiService.updateSeason(editingSeason.id, values);
        message.success("Saison mise à jour avec succès");
      } else {
        // Création
        await apiService.createSeason(content!.id, values);
        message.success("Saison créée avec succès");
      }
      setSeasonModalVisible(false);
      setEditingSeason(null);
      loadSeasonsAndEpisodes(content!.id);
    } catch (error) {
      message.error("Erreur lors de la sauvegarde de la saison");
    }
  };

  const handleEpisodeSubmit = async (values: any) => {
    try {
      let savedEpisode;
      if (editingEpisode) {
        // Mise à jour
        savedEpisode = await apiService.updateEpisode(
          editingEpisode.id,
          values
        );
        message.success("Épisode mis à jour avec succès");
      } else {
        // Création
        savedEpisode = await apiService.createEpisode(
          selectedSeason!.id,
          values
        );
        message.success("Épisode créé avec succès");
      }

      // Si une vidéo a été sélectionnée, l'uploader
      if (episodeVideoFile && savedEpisode) {
        await handleEpisodeVideoUpload(savedEpisode.id);
      }

      setEpisodeModalVisible(false);
      setEditingEpisode(null);
      setSelectedSeason(null);
      setEpisodeVideoFile(null);
      loadSeasonsAndEpisodes(content!.id);
    } catch (error) {
      message.error("Erreur lors de la sauvegarde de l'épisode");
    }
  };

  const handleEpisodeVideoUpload = async (episodeId: number) => {
    if (!episodeVideoFile) {
      return;
    }

    try {
      setUploadingEpisodeVideo(true);
      setEpisodeUploadProgress(0);
      message.info("Upload de la vidéo de l'épisode en cours...");

      // Utiliser l'API de media-processing pour uploader la vidéo de l'épisode
      await apiService.uploadMedia(
        episodeVideoFile,
        "EPISODE",
        episodeId,
        (progress) => {
          setEpisodeUploadProgress(progress);
        }
      );

      setEpisodeUploadProgress(100);
      message.success("Vidéo de l'épisode uploadée avec succès");
      setEpisodeVideoFile(null);
    } catch (error: any) {
      console.error("Erreur lors de l'upload de la vidéo de l'épisode:", error);
      message.error(
        error?.response?.data?.message || "Erreur lors de l'upload de la vidéo"
      );
    } finally {
      setUploadingEpisodeVideo(false);
      setEpisodeUploadProgress(0);
    }
  };

  const handleDeleteSeason = async (seasonId: number) => {
    Modal.confirm({
      title: "Supprimer la saison",
      content: "Êtes-vous sûr de vouloir supprimer cette saison ?",
      onOk: async () => {
        try {
          await apiService.deleteSeason(seasonId);
          message.success("Saison supprimée avec succès");
          loadSeasonsAndEpisodes(content!.id);
        } catch (error) {
          message.error("Erreur lors de la suppression de la saison");
        }
      },
    });
  };

  const handleDeleteEpisode = async (episodeId: number) => {
    Modal.confirm({
      title: "Supprimer l'épisode",
      content: "Êtes-vous sûr de vouloir supprimer cet épisode ?",
      onOk: async () => {
        try {
          await apiService.deleteEpisode(episodeId);
          message.success("Épisode supprimé avec succès");
          loadSeasonsAndEpisodes(content!.id);
        } catch (error) {
          message.error("Erreur lors de la suppression de l'épisode");
        }
      },
    });
  };

  const seasonColumns = [
    {
      title: "Numéro",
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Titre",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Épisodes",
      key: "episodes",
      render: (record: Season) => {
        const seasonEpisodes = episodes.filter(
          (ep) => ep.seasonId === record.id
        );
        return seasonEpisodes.length;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: Season) => (
        <Space>
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedSeason(record);
              setEditingEpisode(null);
              setEpisodeModalVisible(true);
            }}
            title="Ajouter un épisode"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingSeason(record);
              setSeasonModalVisible(true);
            }}
            title="Modifier la saison"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteSeason(record.id)}
            title="Supprimer la saison"
          />
        </Space>
      ),
    },
  ];

  const episodeColumns = [
    {
      title: "Saison",
      dataIndex: "seasonNumber",
      key: "seasonNumber",
    },
    {
      title: "Épisode",
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Titre",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Durée",
      dataIndex: "duration",
      key: "duration",
      render: (duration: number) => {
        if (!duration) return <Text type="secondary">N/A</Text>;
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
      },
    },
    {
      title: "Qualité",
      key: "quality",
      render: (record: Episode) => {
        if (!record.videoResolution && !record.videoQuality) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <div>
            {record.videoResolution && (
              <div>
                <Text strong>{record.videoResolution}</Text>
              </div>
            )}
            {record.videoQuality && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.videoQuality}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Statut vidéo",
      dataIndex: "videoProcessingStatus",
      key: "videoProcessingStatus",
      render: (status: string) => {
        const statusConfig: Record<
          string,
          { color: string; percent: number; label: string }
        > = {
          PENDING: { color: "gray", percent: 0, label: "En attente" },
          PROCESSING: { color: "blue", percent: 50, label: "En traitement" },
          COMPLETED: { color: "green", percent: 100, label: "Complété" },
          FAILED: { color: "red", percent: 0, label: "Échec" },
        };
        const config = statusConfig[status] || statusConfig.PENDING;
        return (
          <div style={{ minWidth: 120 }}>
            <Progress
              percent={config.percent}
              size="small"
              status={
                config.color === "red"
                  ? "exception"
                  : config.color === "green"
                  ? "success"
                  : "active"
              }
              format={() => config.label}
            />
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: Episode) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingEpisode(record);
              setEpisodeModalVisible(true);
            }}
            title="Modifier l'épisode"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteEpisode(record.id)}
            title="Supprimer l'épisode"
          />
        </Space>
      ),
    },
  ];

  if (contentLoading) {
    return <div>Chargement...</div>;
  }

  if (!content) {
    return <div>Contenu non trouvé</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Modifier le contenu
        </Title>
        <Text type="secondary">Modifiez les informations du contenu</Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: "DRAFT",
          hasSubtitles: false,
          hasMultiAudio: false,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Card title="Informations générales" size="small">
              <Form.Item
                name="title"
                label="Titre"
                rules={[
                  { required: true, message: "Le titre est requis" },
                  {
                    max: 255,
                    message: "Le titre ne peut pas dépasser 255 caractères",
                  },
                ]}
              >
                <Input
                  placeholder="Titre du film/série"
                  maxLength={255}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { required: true, message: "La description est requise" },
                  {
                    max: 2000,
                    message:
                      "La description ne peut pas dépasser 2000 caractères",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Description du contenu..."
                  maxLength={2000}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="typeId"
                label="Type de contenu"
                rules={[{ required: true, message: "Le type est requis" }]}
              >
                <Select
                  placeholder="Sélectionner un type"
                  onChange={(value) => {
                    const type = typesData?.data?.find(
                      (t: any) => t.id === value
                    );
                    setSelectedType(type?.title || "");
                  }}
                >
                  {typesData?.data?.map((type: any) => (
                    <Option key={type.id} value={type.id}>
                      {type.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="genreIds"
                label="Genres"
                rules={[
                  { required: true, message: "Au moins un genre est requis" },
                ]}
              >
                <Select mode="multiple" placeholder="Sélectionner les genres">
                  {genresData?.data?.map((genre: any) => (
                    <Option key={genre.id} value={genre.id}>
                      {genre.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="tags"
                label="Tags"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0)
                        return Promise.resolve();
                      const invalidTags = value.filter(
                        (tag: string) => tag.length > 50
                      );
                      if (invalidTags.length > 0) {
                        return Promise.reject(
                          "Chaque tag ne peut pas dépasser 50 caractères"
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  mode="tags"
                  placeholder="Ajouter des tags"
                  tokenSeparators={[","]}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Détails techniques" size="small">
              <Form.Item
                name="releaseDate"
                label="Date de sortie"
                rules={[
                  { required: true, message: "La date de sortie est requise" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Sélectionner une date"
                />
              </Form.Item>

              <Form.Item
                name="originalLanguage"
                label="Langue originale"
                rules={[
                  { required: true, message: "La langue est requise" },
                  {
                    max: 10,
                    message: "La langue ne peut pas dépasser 10 caractères",
                  },
                ]}
              >
                <SelecteurLangue />
              </Form.Item>

              {/* Classification par âge (CMP-03) : exigée par les magasins et
                  préalable au profil enfant. */}
              <Form.Item
                name="ageRating"
                label="Classification par âge"
                tooltip="Âge minimum conseillé. Sans classification, la fiche du magasin doit être calée sur le titre le plus mature du catalogue."
                dependencies={["status"]}
                rules={[
                  ({ getFieldValue }) => ({
                    // Le serveur refuse la publication d'un contenu non
                    // classé ; cette règle l'annonce avant l'envoi. Elle ne
                    // s'applique qu'au passage à « Publié ».
                    required:
                      getFieldValue("status") === "PUBLISHED" &&
                      statutInitial !== "PUBLISHED",
                    message:
                      "La classification est obligatoire pour publier. Choisissez un âge, ou repassez en brouillon.",
                  }),
                ]}
              >
                <SelecteurAgeMinimum />
              </Form.Item>

              <Form.Item
                name="contentDescriptors"
                label="Motifs de la classification"
                tooltip="Violence, langage, contenu sexuel… Les magasins demandent les motifs en plus de l'âge."
              >
                <SelecteurDescripteurs />
              </Form.Item>

              <Form.Item name="status" label="Statut">
                <Select>
                  <Option value="DRAFT">Brouillon</Option>
                  <Option value="PENDING">En attente</Option>
                  <Option value="PUBLISHED">Publié</Option>
                </Select>
              </Form.Item>

              {/* Le libelle passe par `label` : un Form.Item qui contient plus
                  d'un enfant ne relie pas le Switch au formulaire, et la valeur
                  n'est jamais envoyee. */}
              <Form.Item
                name="hasSubtitles"
                valuePropName="checked"
                label="Sous-titres disponibles"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="hasMultiAudio"
                valuePropName="checked"
                label="Audio multilingue"
              >
                <Switch />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        {/* Champs spécifiques aux films */}
        {selectedType === "MOVIE" && (
          <Card title="Détails du film" size="small" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="duration"
                  label="Durée (minutes)"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 300,
                      message: "La durée doit être entre 1 et 300 minutes",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Durée en minutes"
                    min={1}
                    max={300}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="director"
                  label="Réalisateur"
                  rules={[
                    {
                      max: 255,
                      message:
                        "Le nom du réalisateur ne peut pas dépasser 255 caractères",
                    },
                  ]}
                >
                  <Input
                    placeholder="Nom du réalisateur"
                    maxLength={255}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="budget"
                  label="Budget"
                  rules={[
                    {
                      type: "number",
                      min: 0,
                      message: "Le budget doit être positif",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Budget en euros"
                    min={0}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) =>
                      Number(value!.replace(/\$\s?|(,*)/g, "")) as any
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="boxOffice"
                  label="Recettes au box-office"
                  rules={[
                    {
                      type: "number",
                      min: 0,
                      message: "Les recettes doivent être positives",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Recettes en euros"
                    min={0}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) =>
                      Number(value!.replace(/\$\s?|(,*)/g, "")) as any
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {/* Champs spécifiques aux séries */}
        {selectedType === "SERIES" && (
          <Card
            title="Détails de la série"
            size="small"
            style={{ marginTop: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="totalSeasons"
                  label="Nombre de saisons"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 50,
                      message: "Le nombre de saisons doit être entre 1 et 50",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Nombre de saisons"
                    min={1}
                    max={50}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="totalEpisodes"
                  label="Nombre d'épisodes"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 1000,
                      message: "Le nombre d'épisodes doit être entre 1 et 1000",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Nombre d'épisodes"
                    min={1}
                    max={1000}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="averageEpisodeDuration"
                  label="Durée moyenne des épisodes (minutes)"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      max: 180,
                      message: "La durée doit être entre 1 et 180 minutes",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Durée moyenne"
                    min={1}
                    max={180}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Card title="Médias" size="small" style={{ marginTop: 16 }}>
          <Alert
            message="Information importante"
            description="Les métadonnées du contenu se sauvegardent avec le bouton 'Mettre à jour' en bas. Les vidéos (trailer et film complet) s'uploadent séparément avec leurs boutons dédiés."
            type="info"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Title level={5}>Affiche (Poster)</Title>
              <Form.Item label="Uploader une nouvelle affiche">
                <Upload
                  {...posterUploadProps}
                  maxCount={1}
                  accept="image/*"
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />}>
                    Sélectionner une image
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  onClick={handlePosterUpload}
                  disabled={posterFileList.length === 0}
                  loading={uploadingPoster}
                  style={{ marginTop: 12 }}
                >
                  Uploader l'affiche
                </Button>
                {uploadingPoster && (
                  <div style={{ marginTop: 16 }}>
                    <Progress
                      percent={posterUploadProgress}
                      status={
                        posterUploadProgress === 100 ? "success" : "active"
                      }
                      strokeColor={{
                        "0%": "#108ee9",
                        "100%": "#87d068",
                      }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              {(content?.posterUrl || content?.poster_path) ? (
                <div>
                  <Text type="secondary">Affiche actuelle :</Text>
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={content.posterUrl || content.poster_path}
                      alt="Poster"
                      style={{
                        maxWidth: 200,
                        maxHeight: 300,
                        borderRadius: 8,
                        border: "1px solid #d9d9d9",
                      }}
                      onError={(e: any) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Text type="secondary">Aucune affiche</Text>
                </div>
              )}
            </Col>
          </Row>

          <Divider />

          {/* Section Backdrop */}
          <Row gutter={16}>
            <Col span={12}>
              <Title level={5}>Image de fond (Backdrop)</Title>
              <Form.Item label="Uploader un nouveau backdrop (16:9)">
                <Upload
                  {...backdropUploadProps}
                  maxCount={1}
                  accept="image/*"
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />}>
                    Sélectionner une image
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  onClick={handleBackdropUpload}
                  disabled={backdropFileList.length === 0}
                  loading={uploadingBackdrop}
                  style={{ marginTop: 12 }}
                >
                  Uploader le backdrop
                </Button>
                {uploadingBackdrop && (
                  <div style={{ marginTop: 16 }}>
                    <Progress
                      percent={backdropUploadProgress}
                      status={
                        backdropUploadProgress === 100 ? "success" : "active"
                      }
                      strokeColor={{
                        "0%": "#108ee9",
                        "100%": "#87d068",
                      }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              {(content?.backdropUrl || content?.backdrop_path) ? (
                <div>
                  <Text type="secondary">Backdrop actuel :</Text>
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={content.backdropUrl || content.backdrop_path}
                      alt="Backdrop"
                      style={{
                        maxWidth: 360,
                        maxHeight: 200,
                        borderRadius: 8,
                        border: "1px solid #d9d9d9",
                      }}
                      onError={(e: any) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Text type="secondary">Aucun backdrop</Text>
                </div>
              )}
            </Col>
          </Row>

          <Divider />

          {/* Section Bande d'annonce */}
          <Row gutter={16}>
            <Col span={24}>
              <Title level={5}>Bande d'annonce (Trailer)</Title>
              <ProcessingStatusBadge
                status={processingStatus?.trailerProcessingStatus ?? content?.trailerProcessingStatus ?? null}
                progress={processingStatus?.trailerProcessingProgress ?? null}
                stage={processingStatus?.trailerProcessingStage ?? null}
                error={processingStatus?.trailerProcessingError ?? content?.trailerProcessingError ?? null}
                type="trailer"
              />
              <Form.Item label="Fichier de bande d'annonce">
                <Upload {...trailerUploadProps} maxCount={1} accept="video/*">
                  <Button icon={<UploadOutlined />}>
                    Sélectionner la bande d'annonce
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  onClick={handleTrailerUpload}
                  disabled={trailerFileList.length === 0}
                  loading={uploadingTrailer}
                  style={{ marginTop: 12 }}
                >
                  Uploader la bande d'annonce
                </Button>
                {uploadingTrailer && (
                  <div style={{ marginTop: 16 }}>
                    <Progress
                      percent={trailerUploadProgress}
                      status={
                        trailerUploadProgress === 100 ? "success" : "active"
                      }
                      strokeColor={{
                        "0%": "#108ee9",
                        "100%": "#87d068",
                      }}
                    />
                    <Text
                      type="warning"
                      style={{ marginTop: 8, display: "block" }}
                    >
                      Upload de la bande d'annonce en cours...{" "}
                      {trailerUploadProgress}%
                    </Text>
                  </div>
                )}
                {content?.trailerHlsManifestUrl &&
                  trailerFileList.length === 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="success">✓ Bande d'annonce disponible</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          URL: {content.trailerHlsManifestUrl}
                        </Text>
                      </div>
                    </div>
                  )}
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Section Vidéo complète */}
          <Row gutter={16}>
            <Col span={24}>
              <Title level={5}>Vidéo complète du film</Title>
              <ProcessingStatusBadge
                status={processingStatus?.videoProcessingStatus ?? content?.videoProcessingStatus ?? null}
                progress={processingStatus?.videoProcessingProgress ?? null}
                stage={processingStatus?.videoProcessingStage ?? null}
                error={processingStatus?.videoProcessingError ?? content?.videoProcessingError ?? null}
                type="video"
              />
              <Form.Item label="Fichier vidéo complet">
                <Upload {...videoUploadProps} maxCount={1} accept="video/*">
                  <Button icon={<UploadOutlined />}>
                    Sélectionner la vidéo complète
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  onClick={handleVideoUpload}
                  disabled={videoFileList.length === 0}
                  loading={uploadingVideo}
                  style={{ marginTop: 12 }}
                >
                  Uploader la vidéo complète
                </Button>
                {uploadingVideo && (
                  <div style={{ marginTop: 16 }}>
                    <Progress
                      percent={videoUploadProgress}
                      status={
                        videoUploadProgress === 100 ? "success" : "active"
                      }
                      strokeColor={{
                        "0%": "#108ee9",
                        "100%": "#87d068",
                      }}
                    />
                    <Text
                      type="warning"
                      style={{ marginTop: 8, display: "block" }}
                    >
                      Upload de la vidéo complète en cours...{" "}
                      {videoUploadProgress}% - Cela peut prendre plusieurs
                      minutes selon la taille du fichier.
                    </Text>
                  </div>
                )}
                {content?.hlsManifestUrl && videoFileList.length === 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="success">✓ Vidéo complète disponible</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Format HLS: {content.hlsManifestUrl}
                      </Text>
                    </div>
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Annonce : avant-première ou événement mis en avant sur la home */}
        <Card title="Annonce (avant-première, événement)" size="small" style={{ marginTop: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Mise en avant sur l'accueil de l'application"
            description="Activez l'annonce pour afficher ce contenu dans la section « Annonces ». Les informations saisies ici remplacent les textes qui étaient figés dans l'application."
          />
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="isAnnouncement"
                valuePropName="checked"
                label="Afficher ce contenu comme une annonce"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Bandeau"
                name="announcementLabel"
                tooltip="Texte affiché au-dessus du lieu, ex. AVANT-PREMIÈRE"
              >
                <Input placeholder="AVANT-PREMIÈRE" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Lieu" name="announcementVenue">
                <Input placeholder="Théâtre Daniel Sorano" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Date et heure" name="announcementDate">
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: "100%" }}
                  placeholder="Sélectionner"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Sous-titres : fichiers independants, aucun retranscodage declenche */}
        {id && (
          <div style={{ marginTop: 16 }}>
            <SubtitleManager contentId={Number(id)} />
          </div>
        )}

        {/* Gestion des saisons et épisodes pour les séries */}
        {selectedType === "SERIES" && (
          <>
            <Card
              title="Gestion des saisons"
              size="small"
              style={{ marginTop: 16 }}
            >
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingSeason(null);
                    setSeasonModalVisible(true);
                  }}
                >
                  Ajouter une saison
                </Button>
              </div>
              <Table
                columns={seasonColumns}
                dataSource={seasons}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </Card>

            <Card
              title="Gestion des épisodes"
              size="small"
              style={{ marginTop: 16 }}
              extra={
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={() => loadSeasonsAndEpisodes(content!.id)}
                  title="Rafraîchir les épisodes"
                >
                  Rafraîchir
                </Button>
              }
            >
              <Alert
                message="Statut des vidéos"
                description="Le statut des vidéos est mis à jour automatiquement par le service de traitement. Cliquez sur 'Rafraîchir' pour obtenir les dernières informations."
                type="info"
                showIcon
                closable
                style={{ marginBottom: 16 }}
              />
              <Table
                columns={episodeColumns}
                dataSource={episodes}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
              />
            </Card>
          </>
        )}

        <div style={{ marginTop: 24, textAlign: "right" }}>
          <Space>
            <Button onClick={() => navigate("/content")}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Mettre à jour
            </Button>
          </Space>
        </div>
      </Form>

      {/* Modal pour les saisons */}
      <Modal
        title={editingSeason ? "Modifier la saison" : "Ajouter une saison"}
        open={seasonModalVisible}
        onCancel={() => {
          setSeasonModalVisible(false);
          setEditingSeason(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={handleSeasonSubmit}
          initialValues={
            editingSeason
              ? {
                  number: editingSeason.number,
                  title: editingSeason.title,
                }
              : {}
          }
        >
          <Form.Item
            name="number"
            label="Numéro de saison"
            rules={[
              { required: true, message: "Le numéro de saison est requis" },
              {
                type: "number",
                min: 1,
                message: "Le numéro doit être positif",
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Numéro de saison"
              min={1}
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="Titre de la saison"
            rules={[
              { required: true, message: "Le titre de la saison est requis" },
            ]}
          >
            <Input placeholder="Titre de la saison" />
          </Form.Item>
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setSeasonModalVisible(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                {editingSeason ? "Mettre à jour" : "Créer"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal pour les épisodes */}
      <Modal
        title={editingEpisode ? "Modifier l'épisode" : "Ajouter un épisode"}
        open={episodeModalVisible}
        onCancel={() => {
          setEpisodeModalVisible(false);
          setEditingEpisode(null);
          setSelectedSeason(null);
          setEpisodeVideoFile(null);
        }}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          layout="vertical"
          onFinish={handleEpisodeSubmit}
          initialValues={
            editingEpisode
              ? {
                  number: editingEpisode.number,
                  title: editingEpisode.title,
                  duration: editingEpisode.duration,
                }
              : {}
          }
        >
          <Form.Item
            name="number"
            label="Numéro d'épisode"
            rules={[
              { required: true, message: "Le numéro d'épisode est requis" },
              {
                type: "number",
                min: 1,
                message: "Le numéro doit être positif",
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Numéro d'épisode"
              min={1}
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="Titre de l'épisode"
            rules={[
              { required: true, message: "Le titre de l'épisode est requis" },
            ]}
          >
            <Input placeholder="Titre de l'épisode" />
          </Form.Item>
          <Form.Item
            name="duration"
            label="Durée (minutes)"
            rules={[
              {
                type: "number",
                min: 1,
                max: 180,
                message: "La durée doit être entre 1 et 180 minutes",
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Durée en minutes"
              min={1}
              max={180}
            />
          </Form.Item>
          <Form.Item label="Vidéo de l'épisode">
            <MediaUploader
              accept="video/*"
              onFileSelect={(file) => {
                setEpisodeVideoFile(file);
                message.info(`Vidéo sélectionnée: ${file.name}`);
              }}
              maxSize={5000000000}
              autoUpload={false}
            />
            {episodeVideoFile && (
              <div style={{ marginTop: 8 }}>
                <Text type="success">✓ {episodeVideoFile.name}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  (sera uploadée après sauvegarde)
                </Text>
              </div>
            )}
            {uploadingEpisodeVideo && (
              <div style={{ marginTop: 8 }}>
                <Progress
                  percent={episodeUploadProgress}
                  status={episodeUploadProgress === 100 ? "success" : "active"}
                  strokeColor={{
                    "0%": "#108ee9",
                    "100%": "#87d068",
                  }}
                />
                <Alert
                  message={`Upload en cours - ${episodeUploadProgress}%`}
                  description="La vidéo de l'épisode est en cours d'upload vers le serveur. Veuillez patienter..."
                  type="info"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              </div>
            )}
          </Form.Item>
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setEpisodeModalVisible(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                {editingEpisode ? "Mettre à jour" : "Créer"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
